/**
 * Profile URL slug rules (EPD-11640), shared by the API (authoritative) and the
 * app (inline validation as the creator types), so the two can't drift.
 *
 * A slug is the `<slug>` in a creator's public address (`/portfolio/<slug>`).
 * Rules:
 * 3–30 characters of lowercase letters and digits, with single hyphens between
 * groups (no leading, trailing or doubled hyphen). Input is trimmed and
 * lowercased before it is checked.
 */

export const PROFILE_SLUG_MIN_LENGTH = 3;
export const PROFILE_SLUG_MAX_LENGTH = 30;
export const PROFILE_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Minimum time between two slug changes, from the last change. */
export const PROFILE_SLUG_CHANGE_COOLDOWN_DAYS = 30;
/** Window in which switching back to the previous slug is a free undo. */
export const PROFILE_SLUG_UNDO_WINDOW_HOURS = 24;

/**
 * Slugs no creator may take: route segments, product names and words that
 * could pass for an official Aspire page. Compared case-insensitively (input is
 * lowercased first). Add to this list to reserve more.
 */
export const RESERVED_PROFILE_SLUGS: readonly string[] = [
  "admin",
  "administrator",
  "api",
  "app",
  "aspire",
  "aspireiq",
  "auth",
  "login",
  "logout",
  "signin",
  "signup",
  "register",
  "settings",
  "profile",
  "profiles",
  "portfolio",
  "p",
  "p2",
  "o",
  "org",
  "orgs",
  "organization",
  "help",
  "support",
  "about",
  "terms",
  "privacy",
  "legal",
  "security",
  "status",
  "www",
  "mail",
  "email",
  "root",
  "system",
  "null",
  "undefined",
  "new",
  "edit",
  "me",
  "you",
  "official",
  "staff",
  "team",
];

const RESERVED_SET = new Set(RESERVED_PROFILE_SLUGS);

export type ProfileSlugInvalidReason =
  | "too_short"
  | "too_long"
  | "invalid_format";

export type ProfileSlugValidation =
  | { ok: true; slug: string }
  | {
      ok: false;
      code: "SLUG_INVALID";
      reason: ProfileSlugInvalidReason;
      slug: string;
    }
  | { ok: false; code: "SLUG_RESERVED"; slug: string };

/**
 * Typed slug errors returned by the profile service and carried to the client
 * on the tRPC error (`error.data.slugError`). `nextChangeAt` is an ISO string so
 * it survives any transport.
 */
export type ProfileSlugError =
  | {
      code: "SLUG_INVALID";
      reason: ProfileSlugInvalidReason;
      message: string;
    }
  | { code: "SLUG_RESERVED"; message: string }
  | { code: "SLUG_TAKEN"; message: string }
  | { code: "SLUG_RATE_LIMITED"; nextChangeAt: string; message: string };

export type ProfileSlugErrorCode = ProfileSlugError["code"];

const PROFILE_SLUG_ERROR_CODES: readonly string[] = [
  "SLUG_INVALID",
  "SLUG_RESERVED",
  "SLUG_TAKEN",
  "SLUG_RATE_LIMITED",
] satisfies ProfileSlugErrorCode[];

export function isProfileSlugErrorCode(
  code: string,
): code is ProfileSlugErrorCode {
  return PROFILE_SLUG_ERROR_CODES.includes(code);
}

/** Trim and lowercase raw input; the form every rule is checked against. */
export function normalizeProfileSlug(input: string): string {
  return input.trim().toLowerCase();
}

export function isReservedProfileSlug(slug: string): boolean {
  return RESERVED_SET.has(normalizeProfileSlug(slug));
}

/** Validate a slug the creator typed. Returns the normalized slug when valid. */
export function validateProfileSlug(input: string): ProfileSlugValidation {
  const slug = normalizeProfileSlug(input);

  if (slug.length < PROFILE_SLUG_MIN_LENGTH) {
    return { ok: false, code: "SLUG_INVALID", reason: "too_short", slug };
  }
  if (slug.length > PROFILE_SLUG_MAX_LENGTH) {
    return { ok: false, code: "SLUG_INVALID", reason: "too_long", slug };
  }
  if (!PROFILE_SLUG_PATTERN.test(slug)) {
    return { ok: false, code: "SLUG_INVALID", reason: "invalid_format", slug };
  }
  if (RESERVED_SET.has(slug)) {
    return { ok: false, code: "SLUG_RESERVED", slug };
  }
  return { ok: true, slug };
}

/** User-facing copy for a slug that fails the format rules. */
export function profileSlugInvalidMessage(
  reason: ProfileSlugInvalidReason,
): string {
  switch (reason) {
    case "too_short":
      return `Must be at least ${PROFILE_SLUG_MIN_LENGTH} characters.`;
    case "too_long":
      return `Must be ${PROFILE_SLUG_MAX_LENGTH} characters or fewer.`;
    case "invalid_format":
      return "Use only lowercase letters, numbers and single hyphens, with no hyphen at the start or end.";
  }
}

export const PROFILE_SLUG_RESERVED_MESSAGE =
  "This address is reserved. Please choose another.";
export const PROFILE_SLUG_TAKEN_MESSAGE =
  "This address is already taken. Please choose another.";

/**
 * Path the public portfolio is served under (EPD-12500). Before that it was
 * `/p`, which the edge now 301s here; see {@link legacyPortfolioPublicUrls}.
 */
export const PORTFOLIO_PATH_PREFIX = "/portfolio";

function portfolioBase(origin: string, portfolioHost?: string | null): string {
  return portfolioHost
    ? `https://${portfolioHost}`
    : origin.replace(/\/+$/, "");
}

/**
 * Public address of a creator's Portfolio — the link the Toolkit's Share button
 * copies. Shared by the app (Share) and the API (which writes it into the
 * profile's Media Kit on publish, EPD-12223) so the two can't drift.
 *
 * With `portfolioHost` (see {@link resolvePortfolioShareHost}) this is the
 * marketing-domain address `https://<portfolioHost>/portfolio/<slug>`
 * (EPD-10104, EPD-12500). Without one (an environment with no portfolio host,
 * e.g. local dev) it is `<origin>/portfolio/<slug>`, which the app serves
 * itself.
 *
 * `slug` must already be a validated profile slug: it is interpolated as-is,
 * not URL-encoded.
 */
export function portfolioPublicUrl(
  origin: string,
  slug: string,
  portfolioHost?: string | null,
): string {
  return `${portfolioBase(origin, portfolioHost)}${PORTFOLIO_PATH_PREFIX}/${slug}`;
}

/**
 * Earlier portfolio addresses for `slug`: the pre-launch `<origin>/p2/<slug>`,
 * and `/p/<slug>` on the app origin and (with `portfolioHost`) the marketing
 * domain, the address before EPD-12500. Nothing links to them any more, and
 * the edge 301s each to the current address, but Media Kit links written
 * earlier still hold them (EPD-12223), so a slug change or unpublish must
 * still recognise them as ours.
 */
export function legacyPortfolioPublicUrls(
  origin: string,
  slug: string,
  portfolioHost?: string | null,
): string[] {
  const appOrigin = portfolioBase(origin);
  const urls = [`${appOrigin}/p2/${slug}`, `${appOrigin}/p/${slug}`];
  if (portfolioHost) urls.push(`https://${portfolioHost}/p/${slug}`);
  return urls;
}

/**
 * The host portfolio links should point at (API: PORTFOLIO_HOST, app:
 * VITE_PORTFOLIO_HOST), or null when none is configured. Only a bare hostname
 * is accepted: a scheme, path or port would produce a malformed link.
 */
const PORTFOLIO_HOSTNAME =
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

export function resolvePortfolioShareHost(
  host: string | undefined,
): string | null {
  const trimmed = host?.trim().toLowerCase().replace(/\.$/, "");
  if (!trimmed) return null;
  return PORTFOLIO_HOSTNAME.test(trimmed) ? trimmed : null;
}
